import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { completeOAuth, MFA_MESSAGE } from '../auth/oauth';
import { verifyOAuthMfa } from '../api/oauthApi';
import { getSessionRevision, getUserId } from '../auth/tokenStore';

export default function AuthCallbackPage() {
  const navigate = useNavigate();
  const { acceptOAuthSession } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [challenge, setChallenge] = useState<{ tempToken: string; destination: string; expectedUserId: string | null } | null>(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const completion = useRef<ReturnType<typeof completeOAuth> | null>(null);
  useEffect(() => {
    let active = true;
    if (!completion.current) {
      const search = window.location.search;
      // Remove completion codes (and any untrusted legacy token parameters) immediately.
      window.history.replaceState(window.history.state, '', window.location.pathname);
      completion.current = completeOAuth(search);
    }
    completion.current.then(({ session, destination, expectedUserId }) => {
      if (!active) return;
      acceptOAuthSession(session);
      if (session.requiresMfa && session.tempToken) setChallenge({ tempToken: session.tempToken, destination, expectedUserId });
      else if (session.requiresMfa) setError(MFA_MESSAGE);
      else navigate(destination, { replace: true });
    }).catch((reason) => {
      if (active) setError(reason instanceof Error ? reason.message : 'Social sign-in failed. Please start again.');
    });
    return () => { active = false; };
  }, [navigate, acceptOAuthSession]);

  const verify = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!challenge || busy || !/^\d{6}$/.test(code)) return;
    if (challenge.expectedUserId && getUserId() && getUserId() !== challenge.expectedUserId) {
      setError('Your account changed. Please restart sign-in before linking.');
      return;
    }
    setBusy(true);
    setError(null);
    const revision = getSessionRevision();
    try {
      const session = await verifyOAuthMfa(challenge.tempToken, code);
      if (revision !== getSessionRevision()) throw new Error('Your session changed during verification. Please restart sign-in.');
      if (session.requiresMfa) throw new Error('Verification did not complete. Please try again or restart sign-in.');
      if (challenge.expectedUserId && String(session.userId) !== challenge.expectedUserId) {
        throw new Error('The linked account did not match your session. Please restart sign-in.');
      }
      acceptOAuthSession(session);
      setChallenge(null);
      navigate(challenge.destination, { replace: true });
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Verification failed. Please try again or restart sign-in.');
    } finally { setBusy(false); }
  };

  return (
    <div className="center-col" style={{ minHeight: '100vh' }}>
      {challenge ? <form className="auth__form" onSubmit={verify}>
        <h1>Verify your sign-in</h1>
        <p>Enter the six-digit code from your authenticator app. If this attempt expires or reaches its retry limit, restart sign-in.</p>
        <label htmlFor="oauth-mfa-code">Authenticator code</label>
        <input id="oauth-mfa-code" className="input" inputMode="numeric" autoComplete="one-time-code"
          pattern="[0-9]{6}" maxLength={6} required value={code}
          onChange={(event) => setCode(event.target.value.replace(/\D/g, ''))} />
        {error && <p role="alert">{error}</p>}
        <button className="btn btn--primary" disabled={busy || code.length !== 6} type="submit">
          {busy ? 'Verifying…' : 'Verify sign-in'}
        </button>
        <Link to="/login" replace>Restart sign-in</Link>
      </form> : error ? <>
        <p role="alert">{error}</p>
        <Link to="/login" replace>Return to sign in</Link>
      </> : <>
        <span className="spinner" />
        <p className="muted" role="status">Completing sign-in…</p>
      </>}
    </div>
  );
}
